import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "./RejectReasonDialog.module.css";

/**
 * Premium "Reject with reason" dialog. Same visual language as
 * ConfirmDialog (fade + scale-in, danger tone, focus trap, body scroll
 * lock, prefers-reduced-motion fallback) but adds a required textarea
 * the approver MUST fill in — there's no useful "reject without saying
 * why" outcome, the requester needs to know what to fix.
 *
 * Props:
 *   open         boolean
 *   title        string  (required)               — e.g. "Reject edit request"
 *   description  string  (optional callout)        — e.g. "Galo, Ken · ₱5,000.00"
 *   placeholder  string  (textarea hint)
 *   busy         boolean — disables both buttons + shows "Rejecting…"
 *   onConfirm    fn(reason: string)
 *   onCancel     fn()
 *
 * Esc cancels, Cmd/Ctrl+Enter confirms (when reason is non-empty).
 * Default focus = the textarea so the approver can start typing immediately.
 */
export default function RejectReasonDialog({
  open,
  title,
  description,
  placeholder = "Be specific so the requester knows what to fix.",
  busy = false,
  onConfirm,
  onCancel,
}) {
  const textareaRef = useRef(null);
  const cancelRef   = useRef(null);
  const confirmRef  = useRef(null);
  const [reason, setReason] = useState("");

  // Reset whenever the dialog re-opens — never carry over stale text from
  // a previous request that the same approver was reviewing.
  useEffect(() => { if (open) setReason(""); }, [open]);

  useEffect(() => {
    if (!open) return;

    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!busy) onCancel?.();
        return;
      }
      // Submit shortcut — Cmd/Ctrl+Enter from inside the textarea.
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const trimmed = reason.trim();
        if (!busy && trimmed) onConfirm?.(trimmed);
        return;
      }
      // Lightweight focus trap between textarea, Cancel and Confirm so Tab
      // cycles within the dialog rather than wandering into the page behind.
      if (e.key === "Tab") {
        const els = [textareaRef.current, cancelRef.current, confirmRef.current].filter(Boolean);
        if (els.length < 2) return;
        const first = els[0];
        const last  = els[els.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", onKey);

    // Default focus = textarea so the approver can start typing right away.
    const focusT = setTimeout(() => textareaRef.current?.focus(), 0);

    // Lock background scroll while the dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(focusT);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onCancel, onConfirm, reason]);

  if (!open) return null;

  const trimmed   = reason.trim();
  const canSubmit = !busy && !!trimmed;

  return createPortal(
    <div
      className={s.backdrop}>
      <div
        className={s.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="rrd-title"
        onClick={(e) => e.stopPropagation()}>
        <div className={s.iconWrap} aria-hidden="true">
          <span className={s.icon}>✕</span>
        </div>
        <h3 id="rrd-title" className={s.title}>{title}</h3>
        {description && <div className={s.desc}>{description}</div>}

        <label className={s.fieldLabel} htmlFor="rrd-reason">
          Reason for rejecting <span className={s.req}>*</span>
        </label>
        <textarea
          id="rrd-reason"
          ref={textareaRef}
          className={s.textarea}
          rows={4}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
          maxLength={2000}
        />
        <div className={s.hint}>
          The requester will see this. Cmd/Ctrl+Enter to send.
        </div>

        <div className={s.actions}>
          <button
            ref={cancelRef}
            type="button"
            className={s.btnCancel}
            onClick={onCancel}
            disabled={busy}>
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={s.btnDanger}
            onClick={() => { if (canSubmit) onConfirm?.(trimmed); }}
            disabled={!canSubmit}>
            {busy ? "Rejecting…" : "Reject request"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
